import {
  AppThemeType,
  baseScrollbarStyle,
  zIndexes,
} from "@shared/common/Themes/AppTheme";
import { SMALL_MOBILE } from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { css } from "styled-components";
import { newLookTypography as typography } from "@shared/common/Themes/typography";

const space = [4, 8, 16, 32, 64, 128];
const isDarkMode = true;
// Colors
const darkBgColor = "#080B18";
const layoutBgColor = "#0C1022";
const cardBgColor = "#141A2F";
const secondaryCardBgColor = "#1A2139";
const bgColor = layoutBgColor;
const mediumBgColor = "#222A45";
const lightBackground = `${hexToRGB(cardBgColor, 0.4)}`;

const primaryBlue = "#7EA6FF";
const accentBlue = primaryBlue;
const ink = "#0D144E";
const midBlue = "#3D5A99";
const lightBlue = `${hexToRGB(primaryBlue, 0.18)}`;
const primaryGrey = "#EEF1F8";
const mediumGrey = "#B8C0D2";
const secondaryGrey = "#8E99B3";
const lightestGrey = "#2E3654";
const midDark = secondaryGrey;

const primaryText = primaryGrey;
const secondaryText = "#A6AEC2";
const mutedText = "#7E879D";
const canvasBgColor = "#0A0E1C";
const mediumText = mediumGrey;
const lightestText = lightestGrey;

const lightestBorderGrey = `${hexToRGB(secondaryGrey, 0.16)}`;
const lightBorderGrey = `${hexToRGB(secondaryGrey, 0.21)}`;
const mediumBorderGrey = `${hexToRGB(secondaryGrey, 0.27)}`;
const hardBorderGrey = `${hexToRGB(secondaryGrey, 0.45)}`;
const cardBorderColor = `${hexToRGB(secondaryGrey, 0.14)}`;

const basic = "#FFFFFF";
const titleColor = "#F5F7FC";
const textColor = basic;
const buttonText = layoutBgColor;

const layoutFooterColor = lightBlue;
const titleCardBgColor = lightestGrey;
const pieChartBorderColor = lightestGrey;
const tableHoverBgColor = mediumBgColor;
const selectedTableColumnBgColor = mediumBgColor;
const emptyColor = mediumBgColor;
const hoverBgColor = cardBgColor;
const chartTicksColor = `${hexToRGB(secondaryGrey, 0.6)}`;
const chartGridColor = `${hexToRGB(secondaryGrey, 0.2)}`;
const avatarBgColor = "#41362D";
const avatarColor = "#FFA24C";
const activeMenuItemBgColor = `${hexToRGB(primaryBlue, 0.08)}`;
const dashboardCoinsBgColor = mediumBgColor;
const dashboardStatisticsBgColor = cardBgColor;
const disabledInput = `${hexToRGB(cardBgColor, 0.7)}`;
const tagBgColor = mediumBgColor;
const bannerTextColor = textColor;
const statisticsBgColor = darkBgColor;
const checkboxBorderColor = hardBorderGrey;
const filterChipsColor = mediumBgColor;
const activeBgColor = "#56617F";
const filterChipsActiveColor = activeBgColor;
const tvCurrentPriceColor = activeBgColor;
const billingBgColor = layoutBgColor;
const paymentBgColor = "#11172A";
const refreshSpinnerBgColor = lightestGrey;
const boxInCardColor = secondaryCardBgColor;
const highlightBgColor = lightBlue;
const cardLightBgColor = mediumBgColor;
const subMenuTitleBgColor = mediumBgColor;

const blues = [titleCardBgColor, "#CEDCFC", "#B3C9FB", "#8AAEF9", primaryBlue];
// Chart series: the light palette, a step brighter for dark surfaces.
const pieChartColors = ["#6F95F0", "#3DD6EA", "#A78BFA", "#F7B955", "#F472B6"];
const demoTagColors = ["#C584FE", primaryBlue];

const green = "#4CD684";
const red = "#FF7A7A";

const colors = {
  inputBorderColor: lightBorderGrey,
  buttonBgColor: primaryBlue,
  graphColor: primaryBlue,
  secondaryIconBg: mediumBgColor,
  green,
  lightGreen: green,
  darkGreen: green,
  red,
  secondaryRed: "#C13434",
  darkRed: red,
  orange: "#FFA24C",
  yellow: "#FFC554",
  darkYellow: "#D29317",
  gold: "#FFC554",
  lightGrey: "#E0E2E4",
  primary: primaryBlue,
  ink,
  accentBlue,
  white: "#FFFFFF",
  disabledText: hexToRGB(secondaryText, 0.5),
  black: "#000000",
  purple: "#C584FE",
  pink: "#EF66FF",
  primaryGrey,
  mediumGrey,
  secondaryGrey,
  lightestBorderGrey,
  cardBorderColor,
  lightBorderGrey,
  primaryText,
  secondaryText,
  mutedText,
  canvasBgColor,
  lightestText,
  primaryBlue,
  blues,
  pieChartColors,
  cardBgColor,
  bgColor,
  layoutBgColor,
  frameBgColor: darkBgColor,
  layoutFooterColor,
  titleColor,
  textColor,
  basic,
  buttonText,
  lightestGrey,
  titleCardBgColor,
  tableHoverBgColor,
  selectedTableColumnBgColor,
  emptyColor,
  hoverBgColor,
  lightBackground,
  chartTicksColor,
  chartGridColor,
  avatarBgColor,
  avatarColor,
  activeMenuItemBgColor,
  pieChartBorderColor,
  mediumBorderGrey,
  hardBorderGrey,
  dashboardCoinsBgColor,
  dashboardStatisticsBgColor,
  disabledInput,
  midDark,
  tagBgColor,
  tvCurrentPriceColor,
  boxInCardColor,
  lightBlue,
  highlightBgColor,
  subMenuTitleBgColor,
  checkboxBorderColor,
  filterChipsColor,
  bannerTextColor,
  midBlue,
  statisticsBgColor,
  demoTagColors,
  cardLightBgColor,
  refreshSpinnerBgColor,
  filterChipsActiveColor,
  mediumText,
  billingBgColor,
  paymentBgColor,
  mediumBgColor,
  activeBgColor,
  secondaryCardBgColor,
};

const scrollbarStyle = css`
  ${baseScrollbarStyle};
  ul::-webkit-scrollbar-thumb,
  div::-webkit-scrollbar-thumb,
  &::-webkit-scrollbar-thumb {
    border: 3px solid transparent;
    background-color: #8e99b380;
    border-radius: 100px;
    background-clip: content-box;
    @media (max-width: ${SMALL_MOBILE}) {
      border: 1px solid transparent;
    }
  }
`;

const shadows = {
  dashboardCard: "0 1px 2px rgba(0, 0, 0, 0.24)",
  // Shadows do not read on dark: card surfaces use the hairline instead.
  panel: "none",
  symbolCard: "0 2px 20px rgba(0, 0, 0, 0.35)",
  card: "0 6px 24px rgba(0, 0, 0, 0.4)",
  hoverCard: "0 14px 24px rgba(0, 0, 0, 0.45)",
};

const borders = {
  lightest: `1px solid ${colors.lightestBorderGrey}`,
  light: `1px solid ${colors.lightBorderGrey}`,
  medium: `1px solid ${colors.mediumBorderGrey}`,
  hard: `1px solid ${hexToRGB(secondaryGrey, 0.7)}`,
  card: `1px solid ${colors.cardBorderColor}`,
  panel: `1px solid ${colors.cardBorderColor}`,
};

const radii = {
  control: 8,
  card: 12,
  pill: 999,
};

const gradients = {
  pageHaze: `radial-gradient(900px 320px at 88% -120px, ${hexToRGB(
    primaryBlue,
    0.1
  )}, transparent 70%), radial-gradient(700px 260px at 15% -120px, ${hexToRGB(
    colors.purple,
    0.06
  )}, transparent 70%)`,
};

const NewLookDarkTheme: AppThemeType = {
  isDarkMode,
  isNewLook: true,
  isPhone: false,
  hasNewLookBreakpoints: false,
  space,
  colors,
  zIndexes,
  typography,
  shadows,
  borders,
  radii,
  gradients,
  scrollbarStyle,
};

export default NewLookDarkTheme;
